import { FC, ReactNode, useState } from 'react';
import { captureException } from '@sentry/react';
import { BiDuplicate } from 'react-icons/bi';
import { TbEdit, TbExternalLink, TbLink, TbStar, TbStarOff, TbTrash } from 'react-icons/tb';

import { useWorkspaceResourceAccess } from '@/hooks/useWorkspaceResourceAccess';
import { useWorkspaceResourceCreateAccess } from '@/hooks/useWorkspaceResourceCreateAccess';
import RenameResourceModal from '@/shared/components/modals/ResourceActionModals/01-RenameResourceModal';
import DeleteResourceModal from '@/shared/components/modals/ResourceActionModals/03-DeleteResourceModal';
import { toast } from '@/shared/components/toasts/PlatformToast';
import {
    getWorkspaceResourceStatusToggleLabel,
    WorkspaceResourceStatusActionIcon,
} from '@/shared/components/workspace-resources';
import { useFolderStore } from '@/stores';
import { Folder } from '@/types';

import { getWorkspaceResourceRemovalActionLabel, WorkspaceResourceActionMenuItem } from '../helpers';
import WorkspaceResourceActionsDropdown from '../WorkspaceResourceActionsDropdown';

interface FolderActionsDropdownProps {
    folder: Folder;
    children: ReactNode;
    onOpenChange?: (open: boolean) => void;
    open?: boolean;
}

const FolderActionsDropdown: FC<FolderActionsDropdownProps> = ({
    folder,
    children,
    onOpenChange,
    open,
}): JSX.Element => {
    const [renameVisible, setRenameVisible] = useState(false);
    const [deleteVisible, setDeleteVisible] = useState(false);

    const { folders, toggleFolderFavorite, toggleFolderLock, duplicateFolder } = useFolderStore();
    const access = useWorkspaceResourceAccess('folders', folder?.id);
    const createAccess = useWorkspaceResourceCreateAccess('folders', folders?.length ?? 0);

    const handleToggleFavorite = async () => {
        toggleFolderFavorite(folder?.id, !folder?.favorited);
    };

    const handleToggleLock = async () => {
        toggleFolderLock(folder?.id, !folder?.locked);
    };

    const handleDuplicateFolder = async () => {
        if (createAccess.disabled) {
            return;
        }

        duplicateFolder(folder?.id);
    };

    const items: WorkspaceResourceActionMenuItem[] = [
        {
            key: 'favorited',
            icon: folder?.favorited ? <TbStarOff size={'1rem'} /> : <TbStar size={'1rem'} />,
            label: folder?.favorited ? 'Remove from Favorites' : 'Add to Favorites',
            onClick: handleToggleFavorite,
            permission: 'update',
        },
        {
            key: 'locked',
            icon: <WorkspaceResourceStatusActionIcon active={folder?.locked} status='locked' />,
            label: getWorkspaceResourceStatusToggleLabel('locked', folder?.locked),
            onClick: handleToggleLock,
            permission: 'manage',
        },
        {
            key: 'copyLink',
            icon: <TbLink size={'1rem'} />,
            label: 'Copy Link',
            onClick: () => {
                navigator.clipboard
                    .writeText(
                        `${
                            import.meta.env.DEV ? 'http://localhost:3000' : 'https://dashboard.countercyclical.io'
                        }/folders/${folder?.id}`,
                    )
                    .then(
                        () => {
                            toast('Link Copied', {
                                icon: <TbLink size='1.125rem' style={{ marginRight: '0.5rem' }} />,
                                description: folder?.name,
                            });
                        },
                        (error) => {
                            toast('Error Copying Link', {
                                description: folder?.name,
                            });

                            captureException(error);
                        },
                    );
            },
            permission: 'read',
        },
        {
            key: 'openInNewWindow',
            icon: <TbExternalLink size={'1rem'} />,
            label: 'Open in New Window',
            onClick: () => {
                window.open(`/folders/${folder?.id}`, '_blank');
            },
            permission: 'read',
        },
        {
            key: 'rename',
            icon: <TbEdit size={'1rem'} />,
            label: 'Rename',
            onClick: () => {
                setRenameVisible(true);
            },
            permission: 'update',
        },
        {
            key: 'duplicate',
            disabled: createAccess.disabled,
            icon: <BiDuplicate size={'1rem'} />,
            label: 'Duplicate',
            onClick: handleDuplicateFolder,
            permission: 'read',
        },
        // {
        //     key: 'transfer',
        //     icon: <TbArrowsHorizontal size="1rem" />,
        //     label: 'Transfer',
        //     onClick: () => {
        //         return;
        //     },
        //     disabled: true,
        // },
        {
            key: 'delete',
            icon: <TbTrash size={'1rem'} />,
            label: getWorkspaceResourceRemovalActionLabel('folders'),
            onClick: () => {
                setDeleteVisible(true);
            },
            danger: true,
            permission: 'manage',
        },
    ];

    return (
        <>
            <WorkspaceResourceActionsDropdown
                access={access}
                history={{ resourceId: folder.id, resourceType: 'folders', title: folder.name }}
                items={items}
                onOpenChange={onOpenChange}
                open={open}
                resourceLabel='Folder'
            >
                {children}
            </WorkspaceResourceActionsDropdown>
            {/* Action Modals */}
            <RenameResourceModal
                singularResource='folder'
                resource={folder}
                open={renameVisible}
                setOpen={setRenameVisible}
            />
            <DeleteResourceModal
                singularResource='folder'
                resource={folder}
                open={deleteVisible}
                setOpen={setDeleteVisible}
            />
        </>
    );
};

export default FolderActionsDropdown;
